import { utils, WorkBook } from "xlsx";

import { Usergroup } from "types/firebase";
import {
  ClientUploadIssue,
  ClientUploadRow,
  CompanyMatch,
  EmailCheckState,
  ParseClientSheetResult,
  ValidatedClientRow,
} from "types/clientUpload";

// Mirrors server/src/validation/userValidation.ts (register)
export const PASSWORD_MIN = 6;
export const PASSWORD_MAX = 30;

const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Sheet columns, keyed by normalized header text (lowercase, no spaces)
const COLUMNS: { [key: string]: { label: string; headers: string[] } } = {
  platform: { label: "Platform", headers: ["platform"] },
  name: { label: "Name", headers: ["name", "clientname"] },
  company: { label: "Company", headers: ["company"] },
  username: { label: "Username", headers: ["username", "email"] },
  password: { label: "Password", headers: ["password"] },
  added: { label: "Added", headers: ["added", "add"] },
};

const cellText = (value: unknown): string =>
  value === null || value === undefined ? "" : String(value);

const headerKey = (value: unknown) =>
  cellText(value).replace(/\s+/g, "").toLowerCase();

const isBlankRow = (row: unknown[]) =>
  row.every((cell) => cellText(cell).trim() === "");

export const normalizeEmail = (email: string) => email.trim().toLowerCase();

export const isValidEmailFormat = (email: string) => EMAIL_FORMAT.test(email);

// Counts characters like the server's validator.isLength (a surrogate pair is one)
export const passwordLength = (password: string) =>
  password.length - (password.match(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g) || []).length;

export const normalizeCompanyKey = (company: string) =>
  company.trim().replace(/\s+/g, " ").toLowerCase();

// Reads the first sheet. Only rows below the last "Added = Yes" row are new;
// PDF rows and blank rows are skipped.
export const parseClientSheet = (workbook: WorkBook): ParseClientSheetResult => {
  const sheetName = workbook.SheetNames[0];
  const sheet = sheetName ? workbook.Sheets[sheetName] : undefined;

  if (!sheet || !sheet["!ref"]) {
    return { ok: false, error: "The first sheet of this workbook is empty." };
  }

  // raw: false returns each cell's displayed text (dates as shown in Excel,
  // numeric-looking passwords keep leading zeros)
  const firstRow = utils.decode_range(sheet["!ref"]).s.r;
  const rows = utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    raw: false,
    defval: "",
    blankrows: true,
  });
  const toSheetRow = (index: number) => firstRow + index + 1;

  const headerIndex = rows.findIndex((row) => !isBlankRow(row));
  if (headerIndex === -1) {
    return { ok: false, error: "The first sheet of this workbook is empty." };
  }

  const headerPositions: { [header: string]: number } = {};
  rows[headerIndex].forEach((cell, index) => {
    const key = headerKey(cell);
    if (key && headerPositions[key] === undefined) headerPositions[key] = index;
  });

  const columnIndex: { [key: string]: number } = {};
  const missing: string[] = [];
  Object.keys(COLUMNS).forEach((key) => {
    const header = COLUMNS[key].headers.find(
      (h) => headerPositions[h] !== undefined
    );
    if (header) columnIndex[key] = headerPositions[header];
    else missing.push(COLUMNS[key].label);
  });

  if (missing.length > 0) {
    return {
      ok: false,
      error: `Missing column(s): ${missing.join(", ")}. Check the header row (row ${toSheetRow(
        headerIndex
      )}).`,
    };
  }

  const cell = (row: unknown[], key: string) => cellText(row[columnIndex[key]]);
  const dateIndex = headerPositions["datecreated"];

  let cutoffIndex = -1;
  for (let i = rows.length - 1; i > headerIndex; i--) {
    if (cell(rows[i], "added").trim().toLowerCase() === "yes") {
      cutoffIndex = i;
      break;
    }
  }

  if (cutoffIndex === -1) {
    return {
      ok: false,
      error:
        'No row has Added = "Yes", so the cut-off row could not be found. Nothing was imported.',
    };
  }

  const candidates: ClientUploadRow[] = [];
  let pdfSkipped = 0;
  let blankSkipped = 0;

  for (let i = cutoffIndex + 1; i < rows.length; i++) {
    const row = rows[i];

    if (isBlankRow(row)) {
      blankSkipped++;
      continue;
    }

    const platform = cell(row, "platform").trim();
    if (platform.toLowerCase() === "pdf") {
      pdfSkipped++;
      continue;
    }

    const sheetRow = toSheetRow(i);
    const sheetEmail = cell(row, "username");

    candidates.push({
      id: `row-${sheetRow}`,
      sheetRow,
      dateCreated: dateIndex !== undefined ? cellText(row[dateIndex]).trim() : "",
      platform,
      name: cell(row, "name").trim(),
      email: normalizeEmail(sheetEmail),
      sheetEmail,
      sheetCompany: cell(row, "company").trim(),
      companyId: null,
      companyMatch: "none",
      password: cell(row, "password"),
      include: true,
    });
  }

  return {
    ok: true,
    sheetName,
    cutoffRow: toSheetRow(cutoffIndex),
    candidates,
    pdfSkipped,
    blankSkipped,
  };
};

// Exact name first (as createNewUser links by exact name), then a single
// case/spacing-insensitive match. Anything else must be picked by the admin.
export const resolveCompany = (
  sheetCompany: string,
  usergroups: Usergroup[]
): { companyId: string | null; companyMatch: CompanyMatch } => {
  const company = sheetCompany.trim();
  if (!company) return { companyId: null, companyMatch: "none" };

  const exact = usergroups.find((ug) => ug.id && ug.company === company);
  if (exact) return { companyId: exact.id as string, companyMatch: "exact" };

  const key = normalizeCompanyKey(company);
  const fuzzy = usergroups.filter(
    (ug) => ug.id && normalizeCompanyKey(ug.company || "") === key
  );
  if (fuzzy.length === 1) {
    return { companyId: fuzzy[0].id as string, companyMatch: "fuzzy" };
  }

  return { companyId: null, companyMatch: "none" };
};

export const applyCompanyResolution = (
  rows: ClientUploadRow[],
  usergroups: Usergroup[]
): ClientUploadRow[] =>
  rows.map((row) => ({ ...row, ...resolveCompany(row.sheetCompany, usergroups) }));

// Mirrors the license caps in manage-clients/NewUserForm.tsx
export const getSeatLimit = (license?: string): number | null => {
  if (license === "site") return 5;
  if (license === "corporate" || license === "enterprise") return 100;
  return null;
};

export const validateClientRows = (
  rows: ClientUploadRow[],
  context: {
    usergroups: Usergroup[];
    emailChecks: { [email: string]: EmailCheckState };
  }
): ValidatedClientRow[] => {
  const { usergroups, emailChecks } = context;

  const groupsById: { [id: string]: Usergroup } = {};
  usergroups.forEach((ug) => {
    if (ug.id) groupsById[ug.id] = ug;
  });

  // Sheet rows per email, among included rows only
  const rowsByEmail: { [email: string]: number[] } = {};
  rows.forEach((row) => {
    const email = normalizeEmail(row.email);
    if (!row.include || !email) return;
    rowsByEmail[email] = (rowsByEmail[email] || []).concat(row.sheetRow);
  });

  const seatsTaken: { [id: string]: number } = {};

  return rows.map((row) => {
    const issues: ClientUploadIssue[] = [];
    const error = (message: string) => issues.push({ severity: "error", message });
    const warning = (message: string) =>
      issues.push({ severity: "warning", message });

    const email = normalizeEmail(row.email);
    const group = row.companyId ? groupsById[row.companyId] : undefined;

    if (!row.name.trim()) error("Name is required.");

    if (!email) {
      error("Email is required.");
    } else if (!isValidEmailFormat(email)) {
      error("Email format is invalid.");
    } else {
      const check = emailChecks[email];
      if (check === "exists") error("Already has an account. Will not be created.");
      else if (check === "invalid") error("Email was rejected by the server.");
      else if (check === "failed")
        error("Could not check for an existing account. Retry the check.");
      else if (check === "pending") error("Checking for an existing account...");
      else if (check !== "new")
        error("Not checked for an existing account yet. Click outside the email field to check.");
    }

    if (row.include && email) {
      const others = (rowsByEmail[email] || []).filter((r) => r !== row.sheetRow);
      if (others.length > 0) {
        error(`Duplicate email in this upload (also row ${others.join(", ")}).`);
      }
    }

    const length = passwordLength(row.password);
    if (length < PASSWORD_MIN || length > PASSWORD_MAX) {
      error(
        `Password must be ${PASSWORD_MIN}-${PASSWORD_MAX} characters (currently ${length}).`
      );
    } else if (row.password !== row.password.trim()) {
      warning(
        "Password has leading or trailing spaces. The client must type them exactly."
      );
    }

    if (!group) {
      error(
        row.sheetCompany
          ? `Company "${row.sheetCompany}" not found. Pick an existing company.`
          : "Company is required. Pick an existing company."
      );
    } else if (row.companyMatch === "fuzzy") {
      warning(
        `Company matched ignoring case/spacing: "${row.sheetCompany}" -> "${group.company}".`
      );
    }

    if (row.platform.toLowerCase() !== "ido") {
      warning(
        row.platform
          ? `Platform is "${row.platform}", expected IDO.`
          : "Platform is blank, expected IDO."
      );
    }

    // Only included rows without other errors take a seat
    const hasError = issues.some((issue) => issue.severity === "error");
    if (row.include && group && group.id && !hasError) {
      const limit = getSeatLimit(group.license);
      if (limit !== null) {
        const used = (group.users ? group.users.length : 0) + (seatsTaken[group.id] || 0);
        if (used < limit) {
          seatsTaken[group.id] = (seatsTaken[group.id] || 0) + 1;
        } else {
          error(
            `Seat limit reached: ${group.company} has a ${group.license} license (max ${limit} users).`
          );
        }
      }
    }

    const status = !row.include
      ? "excluded"
      : issues.some((issue) => issue.severity === "error")
      ? "error"
      : issues.length > 0
      ? "warning"
      : "ready";

    return { ...row, issues, status };
  });
};

export const isCreatable = (row: ValidatedClientRow) =>
  row.include && row.status !== "error";

export const summarizeRows = (rows: ValidatedClientRow[]) => ({
  ready: rows.filter((row) => row.status === "ready").length,
  warning: rows.filter((row) => row.status === "warning").length,
  error: rows.filter((row) => row.status === "error").length,
  excluded: rows.filter((row) => row.status === "excluded").length,
  creatable: rows.filter(isCreatable).length,
});
